import React, { useState, useEffect, useRef } from 'react';
import './StockSelector.css';

/**
 * StockSelector — Grid of clickable stocks for bundling.
 *
 * Ships with the 7 server-tracked tickers pre-loaded.  Users can also search
 * for *any* ticker (SPY, COIN, BRK.B, etc.) via the search bar; live prices
 * are fetched from the /api/lookup endpoint and the card is added to the grid
 * for the current session.
 */
const StockSelector = ({ onSelectStocks: onSelectStocksCallback }) => {
  const [stocks, setStocks]               = useState([]);
  const [selectedStocks, setSelectedStocks] = useState({}); // { symbol: quantity }
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);

  // Custom symbol search
  const [searchInput, setSearchInput]   = useState('');
  const [searching, setSearching]       = useState(false);
  const [searchError, setSearchError]   = useState(null);
  const [customStocks, setCustomStocks] = useState([]); // user-added stocks

  const searchInputRef = useRef(null);

  // ── Load tracked stocks ────────────────────────────────────────────────────
  const fetchStocks = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/stocks');
      if (!response.ok) throw new Error('Failed to fetch stocks');
      setStocks(await response.json());
      setError(null);
    } catch (err) {
      setError(err.message);
      setStocks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStocks(); }, []);

  // ── Custom symbol lookup ───────────────────────────────────────────────────
  const handleSymbolSearch = async (e) => {
    e.preventDefault();
    const sym = searchInput.trim().toUpperCase();
    if (!sym) return;

    // Already in the grid (tracked or custom)?
    const allSymbols = [...stocks, ...customStocks].map(s => s.symbol);
    if (allSymbols.includes(sym)) {
      setSearchError(`${sym} is already in the list`);
      return;
    }

    setSearching(true);
    setSearchError(null);

    try {
      const res = await fetch(`/api/lookup/${encodeURIComponent(sym)}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `${sym} not found`);
      }
      const stock = await res.json();
      setCustomStocks(prev => [...prev, stock]);
      setSearchInput('');
      searchInputRef.current?.focus();
    } catch (err) {
      setSearchError(err.message);
    } finally {
      setSearching(false);
    }
  };

  const removeCustomStock = (symbol) => {
    setCustomStocks(prev => prev.filter(s => s.symbol !== symbol));
    // Also deselect if it was selected
    setSelectedStocks(prev => {
      const next = { ...prev };
      delete next[symbol];
      return next;
    });
  };

  // ── Selection helpers ──────────────────────────────────────────────────────
  const toggleStockSelection = (symbol) => {
    setSelectedStocks(prev => {
      const next = { ...prev };
      if (next[symbol] !== undefined) delete next[symbol];
      else next[symbol] = 1;
      return next;
    });
  };

  const updateStockQuantity = (symbol, quantity) => {
    setSelectedStocks(prev => ({
      ...prev,
      [symbol]: Math.max(1, Math.min(1000, parseInt(quantity) || 1))
    }));
  };

  const handleViewBundle = () => {
    if (Object.keys(selectedStocks).length === 0) return;
    const allStocks = [...stocks, ...customStocks];
    const selectedStockObjects = allStocks
      .filter(s => selectedStocks[s.symbol] !== undefined)
      .map(s => ({ ...s, quantity: selectedStocks[s.symbol] }));
    onSelectStocksCallback(selectedStockObjects);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="stock-selector-container error-state">
        <div className="error-message">Error loading stocks: {error}</div>
      </div>
    );
  }

  const selectedCount = Object.keys(selectedStocks).length;
  const allStocks     = [...stocks, ...customStocks];

  return (
    <div className="stock-selector-container">
      <div className="selector-header">
        <h1>📈 Stock Bundle Viewer</h1>
        <p>Select stocks and set quantities to see them bundled together over time</p>
      </div>

      {loading ? (
        <div className="loading-message">Loading stocks...</div>
      ) : (
        <>
          {/* ── Custom symbol search ── */}
          <form className="symbol-search-bar" onSubmit={handleSymbolSearch}>
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder="Add any ticker (e.g. SPY, COIN, QQQ)"
              value={searchInput}
              onChange={e => { setSearchInput(e.target.value.toUpperCase()); setSearchError(null); }}
              maxLength={6}
              disabled={searching}
            />
            <button
              type="submit"
              className="search-btn"
              disabled={!searchInput.trim() || searching}
            >
              {searching ? '…' : '+ Add'}
            </button>
          </form>

          {searchError && (
            <div className="search-error">{searchError}</div>
          )}

          {/* ── Stock grid (tracked + custom) ── */}
          <div className="stock-grid">
            {allStocks.map((stock) => {
              const isSelected = selectedStocks[stock.symbol] !== undefined;
              const quantity   = selectedStocks[stock.symbol] || 1;
              const isCustom   = customStocks.some(s => s.symbol === stock.symbol);

              return (
                <div
                  key={stock.symbol}
                  className={`stock-card ${isSelected ? 'selected' : ''}`}
                >
                  {isCustom && (
                    <button
                      className="remove-custom-btn"
                      title="Remove from list"
                      onClick={(e) => { e.stopPropagation(); removeCustomStock(stock.symbol); }}
                    >
                      ×
                    </button>
                  )}

                  <div
                    className="stock-card-content"
                    onClick={() => toggleStockSelection(stock.symbol)}
                  >
                    <div className="stock-checkbox">
                      <div className={`checkbox ${isSelected ? 'checked' : ''}`}>
                        {isSelected ? '✓' : ''}
                      </div>
                    </div>
                    <div className="stock-logo">{stock.symbol[0]}</div>
                    <div className="stock-info">
                      <div className="stock-symbol">
                        {stock.symbol}
                        {isCustom && <span className="custom-badge">custom</span>}
                      </div>
                      <div className="stock-name">{stock.name}</div>
                      <div className="stock-price">${stock.price.toFixed(2)}</div>
                      <div className={`stock-change ${stock.changePercent >= 0 ? 'positive' : 'negative'}`}>
                        {stock.changePercent >= 0 ? '+' : ''}{stock.changePercent.toFixed(2)}%
                      </div>
                    </div>
                  </div>

                  {isSelected && (
                    <div
                      className="quantity-input-wrapper"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <label className="quantity-label">Qty:</label>
                      <input
                        type="number"
                        min="1"
                        max="1000"
                        value={quantity}
                        onChange={(e) => updateStockQuantity(stock.symbol, e.target.value)}
                        className="quantity-input"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="selector-footer">
            <div className="selection-count">
              {selectedCount > 0
                ? `${selectedCount} stock${selectedCount > 1 ? 's' : ''} selected`
                : 'Select stocks to continue'}
            </div>
            <button
              className="view-bundle-btn"
              onClick={handleViewBundle}
              disabled={selectedCount === 0}
            >
              {selectedCount > 0 ? `View Bundle (${selectedCount})` : 'Select stocks'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default StockSelector;
